import { ComponentPropsWithRef } from 'react';
import styled from 'styled-components';
import { colors } from '@ardoq/design-tokens';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { DEFAULT_ARDOQ_I18NLOCALE, t, type I18nLocale } from '@ardoq/i18n';
import { DRAG_BUTTON_CLASS_NAME, DRAG_HANDLE_ATTRIBUTE } from './const';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { UnstyledButton } from './Button';

type DragButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
  isDisabled?: boolean;
  iconSize?: IconSize;
  i18nLocale?: I18nLocale;
};

const StyledDragButton = styled(UnstyledButton)`
  display: inline-flex;
  align-items: center;
  cursor: grab;
  color: ${colors.grey500};
  &:hover {
    color: ${colors.grey700};
  }
  &:active {
    cursor: grabbing;
  }
  &[aria-disabled='true'] {
    color: ${colors.iconActionDisabled};
    cursor: default;
    user-select: none;
    &:hover {
      color: ${colors.iconActionDisabled};
    }
  }
`;

export const DragButton = ({
  iconSize = IconSize.LARGE,
  className,
  i18nLocale = DEFAULT_ARDOQ_I18NLOCALE,
  isDisabled,
  draggable,
  ...props
}: DragButtonProps) => (
  <StyledDragButton
    type="button"
    // `aria-disabled` rather than `disabled` keeps the handle focusable, so a
    // keyboard user tabbing a list gets a stop on every row and hears which
    // ones cannot be reordered, instead of the tab order silently going ragged.
    aria-disabled={isDisabled || undefined}
    draggable={isDisabled ? false : draggable}
    className={[DRAG_BUTTON_CLASS_NAME, className].filter(Boolean).join(' ')}
    {...props}
    // The behavioural marker drag-and-drop implementations look for (the class
    // name above is only a styling hook). Stamped after `props` on purpose: a
    // caller must not be able to unset the marker and silently break dragging.
    {...{ [DRAG_HANDLE_ATTRIBUTE]: '' }}
  >
    <Icon iconName={IconName.DRAG_INDICATOR} iconSize={iconSize} />
    <VisuallyHidden>
      {t(i18nLocale, 'button:dragButtonAriaLabel', 'Drag to reorder')}
    </VisuallyHidden>
  </StyledDragButton>
);
